import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import useUserStore from '../store/user';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const profileRequest = useRef(0);

  const clearProfile = useCallback(() => {
    profileRequest.current += 1;
    setUserProfile(null);
    setProfileLoading(false);
    setProfileError(null);
  }, []);

  const loadProfile = useCallback(async (userId) => {
    if (!userId) {
      clearProfile();
      return null;
    }

    const requestId = ++profileRequest.current;
    setProfileLoading(true);
    setProfileError(null);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      if (profileRequest.current === requestId) setUserProfile(data || null);
      return data || null;
    } catch (error) {
      console.error('[auth] Profile load failed:', error.message);
      if (profileRequest.current === requestId) {
        setUserProfile(null);
        setProfileError(error);
      }
      return null;
    } finally {
      if (profileRequest.current === requestId) setProfileLoading(false);
    }
  }, [clearProfile]);

  useEffect(() => {
    let active = true;
    let authEventSeen = false;

    const applySession = (nextSession) => {
      if (!active) return;
      setSession(nextSession || null);
      setUser(nextSession?.user || null);
      setLoading(false);
      if (nextSession?.user?.id) loadProfile(nextSession.user.id);
      else clearProfile();
    };

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      authEventSeen = true;
      applySession(nextSession);
    });

    supabase.auth.getSession().then(({ data, error }) => {
      if (!active || authEventSeen) return;
      if (error) console.error('[auth] Session restoration failed:', error.message);
      applySession(data?.session || null);
    });

    return () => {
      active = false;
      authListener?.subscription?.unsubscribe();
    };
  }, [clearProfile, loadProfile]);

  const signIn = useCallback((email, password) => (
    supabase.auth.signInWithPassword({ email, password })
  ), []);

  const signUp = useCallback((email, password, options = {}) => (
    supabase.auth.signUp({ email, password, options })
  ), []);

  const signInWithOAuth = useCallback((provider, options = {}) => (
    supabase.auth.signInWithOAuth({ provider, options })
  ), []);

  const resetPasswordForEmail = useCallback((email, options = {}) => (
    supabase.auth.resetPasswordForEmail(email, options)
  ), []);

  const signOut = useCallback(async () => {
    const result = await supabase.auth.signOut();
    if (!result.error) {
      setSession(null);
      setUser(null);
      clearProfile();
      useUserStore.getState().signOut();
      localStorage.removeItem('user');
    }
    return result;
  }, [clearProfile]);

  const value = {
    user,
    session,
    userProfile,
    loading,
    profileLoading,
    profileError,
    signIn,
    signUp,
    signInWithOAuth,
    resetPasswordForEmail,
    signOut,
    refreshProfile: () => loadProfile(user?.id),
    isAuthenticated: Boolean(user),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
